import {
  AgentFormResponseMode,
  AgentThreadSourceMode,
  THumanConfirmStepDefinition,
  TSysAgentStepDefinition,
  TWorkflowDefinition,
  TWorkflowStepTransition,
  WorkflowStepType,
} from '@commenda/types';

import type { TaskParty } from '@prisma/client';

import { ServiceAuditorStepHelper } from '../../workflow/helpers/ServiceAuditorStepHelper';
import { WorkflowHandlerID } from '../../workflow/ui/handles';
import {
  TBuildDraftAuditorStepsParams,
  TDraftAuditorStepIds,
  TDraftAuditorSteps,
} from '../types/draft-auditor';

export class ServiceAuditorEditorHelper {
  // Palette drag payload type for the auditor preset; never stored as a step type.
  static readonly DRAFT_AUDITOR_TEMPLATE_NODE_TYPE = 'template.draft_auditor';

  static readonly AGENT_STEP_NAME = 'Commenda draft review';

  static readonly APPROVAL_STEP_NAME = 'Approve draft for the customer';

  // The value HumanStepAssigneeConfig writes for Admin; a type-only import keeps the Prisma client out of tests.
  static readonly ADMIN_ASSIGNEE_TYPE: Lowercase<TaskParty> = 'admin';

  // The approval step sits two rows down, leaving the row between for the wait step the backend inserts on save.
  static readonly APPROVAL_STEP_Y_OFFSET = 300;

  static insertDraftAuditor(
    workflow: Readonly<TWorkflowDefinition>,
    params: Readonly<TBuildDraftAuditorStepsParams>
  ): TWorkflowDefinition {
    const { agentStep, approvalStep, transitions } =
      ServiceAuditorEditorHelper.buildDraftAuditorSteps(params);

    return {
      ...workflow,
      steps: [...workflow.steps, agentStep, approvalStep],
      stepTransitions: [...workflow.stepTransitions, ...transitions],
    };
  }

  // The draft step is unknown at drop time (nothing connects to the new steps yet), so the author picks it on the
  // auditor step. The approval step blocks the next step until an admin completes it. Saving splices the
  // auditor's wait step in between, so the canvas shows it once the version is reloaded.
  private static buildDraftAuditorSteps({
    agentStepId,
    position,
  }: Readonly<TBuildDraftAuditorStepsParams>): TDraftAuditorSteps {
    const ids =
      ServiceAuditorEditorHelper.deriveDraftAuditorStepIds(agentStepId);

    const agentStep: TSysAgentStepDefinition = {
      id: ids.agentStepId,
      type: WorkflowStepType.SYS_AI_AGENT,
      name: ServiceAuditorEditorHelper.AGENT_STEP_NAME,
      profileKey: ServiceAuditorStepHelper.SERVICE_AUDITOR_PROFILE_KEY,
      readOnly: true,
      formResponseSource: { mode: AgentFormResponseMode.ALL_PREVIOUS },
      uploadSource: { mode: AgentFormResponseMode.ALL_PREVIOUS },
      threadSource: { mode: AgentThreadSourceMode.ALL },
      ui: { reactFlow: { position: { x: position.x, y: position.y } } },
    };

    const approvalStep: THumanConfirmStepDefinition = {
      id: ids.approvalStepId,
      type: WorkflowStepType.HUMAN_CONFIRM,
      name: ServiceAuditorEditorHelper.APPROVAL_STEP_NAME,
      assigneeConfig: { type: ServiceAuditorEditorHelper.ADMIN_ASSIGNEE_TYPE },
      ui: {
        reactFlow: {
          position: {
            x: position.x,
            y: position.y + ServiceAuditorEditorHelper.APPROVAL_STEP_Y_OFFSET,
          },
        },
      },
    };

    return {
      agentStep,
      approvalStep,
      transitions: [
        ServiceAuditorEditorHelper.buildTransition(
          ids.agentToApprovalTransitionId,
          ids.agentStepId,
          ids.approvalStepId
        ),
      ],
    };
  }

  private static deriveDraftAuditorStepIds(
    agentStepId: string
  ): TDraftAuditorStepIds {
    return {
      agentStepId,
      approvalStepId: `${agentStepId}__approval`,
      agentToApprovalTransitionId: `${agentStepId}__t-to-approval`,
    };
  }

  private static buildTransition(
    id: string,
    fromStepId: string,
    toStepId: string
  ): TWorkflowStepTransition {
    return {
      id,
      fromStepId,
      toStepId,
      ui: {
        reactFlow: {
          handle: {
            sourceHandle: WorkflowHandlerID.SOURCE_BOTTOM,
            targetHandle: WorkflowHandlerID.TARGET_TOP,
          },
        },
      },
    };
  }
}
